import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import Select from "react-select";
import { usePDFDownload, useExcelDownload } from "../../../helper/HelperTwo";
import { TableCellsIcon } from "@heroicons/react/24/outline";

const QuotationReportList = () => {
  const [loading, setLoading] = useState(false);
  const tabName = localStorage.getItem("tab_name");
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [quotationAmountByTypeOfWork, setQuotationAmountByTypeOfWork] =
    useState([]);
  const [todayQuotationList, setTodayQuotationList] = useState([]);
  const [unfollowedQuotationList, setUnfollowedQuotationList] = useState([]);
  const [typeOfWork, setTypeOfWork] = useState([]);
  const [futureQuotationList, setFutureQuotationList] = useState([]);
  const { PDFDownload } = usePDFDownload();
  const { excelDownload } = useExcelDownload();
  const [fullscreenTable, setFullscreenTable] = useState(null);
  const [colors, setColors] = useState([]);
  const [quotationFilter, setQuotationFilter] = useState({
    date_from: "",
    date_to: "",
    date_filter: "",
    type_of_work: "",
    type: tabName || "",
    project_manager: "",
  });
  const [statsDetails, setStatsDetails] = useState([]);
   const [projectManagerList, setProjectManagerList] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setQuotationFilter({
      ...quotationFilter,
      [name]: value,
    });
  };

  const fetchLead = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const response = await axios.get("public/api/report/quotations?type=" + tabName, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const typeOfWork = await axios.get("public/api/type-of-works/list", {
        headers: { Authorization: `Bearer ${token}` },
      });

       const projectManager = await axios.get("public/api/project-manager/list", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjectManagerList(projectManager?.data);
      setTypeOfWork(typeOfWork?.data);
      setQuotationAmountByTypeOfWork(
        response?.data?.quotationAmountByTypeOfWork
      );
      setUnfollowedQuotationList(response?.data?.unfollowedQuotations);
      setTodayQuotationList(response?.data?.todayQuotations);
      setFutureQuotationList(response?.data?.futureQuotations);
      setColors(generateColors(response?.data?.typeOfWork));
    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/crm/");
      }
      setError(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLead();
  }, []);

  const handleFilter = async () => {
    try {
      const token = localStorage.getItem("token");
      setLoading(true);

      const response = await axios.get("public/api/report/quotations", {
        params: {
          date_from: quotationFilter.date_from || "",
          date_to: quotationFilter.date_to || "",
          date_filter: quotationFilter.date_filter || "",
          type_of_work: quotationFilter.type_of_work || "",
          project_manager: quotationFilter.project_manager || "",
          type: quotationFilter.type || "",
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      setQuotationAmountByTypeOfWork(
        response?.data?.quotationAmountByTypeOfWork
      );
      setUnfollowedQuotationList(response?.data?.unfollowedQuotations);
      setTodayQuotationList(response?.data?.todayQuotations);
      setFutureQuotationList(response?.data?.futureQuotations);
    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/crm/");
      }
      setError(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setQuotationFilter({
      date_from: "",
      date_to: "",
      date_filter: "",
      type_of_work: "",
      type: tabName || "",
    });
    fetchLead();
  };

  const salesServiceOptions = [
    {
      value: "sales",
      name: "Sales",
    },
    {
      value: "service",
      name: "Service",
    },
  ];

  const downloadPDF = (QuotationPdfType, title) => {
    const params = {
      date_from: quotationFilter.date_from || "",
      date_to: quotationFilter.date_to || "",
      date_filter: quotationFilter.date_filter || "",
      type_of_work: quotationFilter.type_of_work || "",
      type: quotationFilter.type || "",
      project_manager: quotationFilter.project_manager || "",
      pdf_type: QuotationPdfType,
      pdf_title: title,
    };
    PDFDownload("report/quotations", "quotations.pdf", params, setLoading);
  };

  const downloadExcel = (leadExcelType, title) => {
    const params = {
      date_from: quotationFilter.date_from || "",
      date_to: quotationFilter.date_to || "",
      date_filter: quotationFilter.date_filter || "",
      type_of_work: quotationFilter.type_of_work || "",
      type: quotationFilter.type || "",
      project_manager: quotationFilter.project_manager || "",
      pdf_type: leadExcelType || "",
      excel_title: title,
    }
    excelDownload('report/quotations', 'leads.xlsx', params, setLoading);
  }

  const generateColors = (count) => {
    const colors = [];
    // for (let i = 0; i < count; i++) {
    //   const color = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
    //   colors.push(color);
    // }

    for (let i = 0; i < count; i++) {
      const hue = Math.floor(Math.random() * 360); // full color range
      const saturation = Math.floor(Math.random() * 40) + 70; // 70–100% = very colorful
      const lightness = Math.floor(Math.random() * 30) + 25; // 60–90% = bright

      const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      colors.push(color);
    }

    return colors;
  };

  const getRandomColorClass = () => {
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const randomColorMap = useMemo(() => {
    const map = {};
    unfollowedQuotationList.forEach((lead) => {
      // lead?.type_of_work?.forEach((work) => {
      //   if (!map[work.id]) {
      //     map[work.id] = getRandomColorClass();
      //   }
      // });
      map[lead?.type_of_work] = getRandomColorClass();
    });
    return map;
  }, [unfollowedQuotationList]);

  const todayRandomColorMap = useMemo(() => {
    const map = {};
    todayQuotationList.forEach((lead) => {
      // lead?.type_of_work?.forEach((work) => {
      //   if (!map[work.id]) {
      //     map[work.id] = getRandomColorClass();
      //   }
      // });
      map[lead?.type_of_work] = getRandomColorClass();
    });
    return map;
  }, [todayQuotationList]);

  const futureRandomColorMap = useMemo(() => {
    const map = {};
    futureQuotationList.forEach((lead) => {
      // lead?.type_of_work?.forEach((work) => {
      //   if (!map[work.id]) {
      //     map[work.id] = getRandomColorClass();
      //   }
      // });
      map[lead?.type_of_work] = getRandomColorClass();
    });
    return map;
  }, [futureQuotationList]);

  if (loading) {
    return (
      <div class="h-screen bg-white">
        <div class="flex justify-center items-center h-full">
          <img
            class="h-16 w-16"
            src="https://icons8.com/preloaders/preloaders/1488/Iphone-spinner-2.gif"
            alt="loader"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen bg-white flex flex-col justify-center items-center">
        <p className="mb-4 text-red-600 font-medium">Something went wrong.</p>
        <button
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          onClick={() => {
            setError(null); // hide error while retrying
            fetchLead(); // retry API
          }}
        >
          Reload
        </button>
      </div>
    );
  }

  return (
    <div>
      <div>
        <h2 className="text-md sm:text-2xl font-semibold mb-4">
          Quotation Report
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mt-4">
          <div className="space-y-3 ">
            <div className="">
              <label className="block text-sm font-medium text-gray-700">
                Filter by date
              </label>
              <select
                name="date_filter"
                value={quotationFilter.date_filter}
                onChange={handleChange}
                className="border border-gray-300 p-2 rounded w-full mt-2"
              >
                <option value="" className="text-gray-300">
                  -- Select --
                </option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="this_year">This Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>

          {quotationFilter.date_filter === "custom" && (
            <>
              <div className="space-y-3 ">
                <div className="">
                  <label className="block text-sm font-medium">Date From</label>
                  <input
                    type="date"
                    name="date_from"
                    value={quotationFilter.date_from}
                    onChange={handleChange}
                    className="mt-1 w-full rounded border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                  />
                </div>
              </div>
              <div className="space-y-3 ">
                <div className="w-full">
                  <label className="block text-sm font-medium">Date To</label>
                  <input
                    type="date"
                    name="date_to"
                    value={quotationFilter.date_to}
                    onChange={handleChange}
                    className="mt-1 w-full rounded border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                  />
                </div>
              </div>
            </>
          )}

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type of Work
            </label>
            <Select
              name="type_of_work"
              options={typeOfWork?.map((work) => ({
                value: work.name,
                label: work.name,
              }))}
              value={
                typeOfWork
                  ?.map((work) => ({ value: work.name, label: work.name }))
                  .find(
                    (option) => option?.value === quotationFilter?.type_of_work
                  ) || null
              }
              onChange={(selectedOption) =>
                handleChange({
                  target: {
                    name: "type_of_work",
                    value: selectedOption?.value || "",
                  },
                })
              }
              isMulti={false}
              isClearable={true}
              placeholder="Select Type of Work"
              className="react-select-container"
              classNamePrefix="select"
            />
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sales / Service
            </label>
            <Select
              name="type"
              options={salesServiceOptions?.map((item) => ({
                value: item.value,
                label: item.name,
              }))}
              value={
                salesServiceOptions
                  ?.map((item) => ({
                    value: item.value,
                    label: item.name,
                  }))
                  .find((option) => option.value === quotationFilter.type) ||
                null
              }
              onChange={(selectedOption) =>
                handleChange({
                  target: {
                    name: "type",
                    value: selectedOption?.value || "",
                  },
                })
              }
              isMulti={false}
              isClearable={true}
              placeholder="Select Sales / Service"
              className="react-select-container"
              classNamePrefix="select"
            />
          </div>
           <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Manager
            </label>
            <Select
              name="project_manager"
              options={projectManagerList?.map((item) => ({
                value: item.name,
                label: item.name,
              }))}
              value={
                projectManagerList
                  ?.map((item) => ({
                    value: item.name,
                    label: item.name,
                  }))
                  .find(
                    (option) => option.value === quotationFilter?.project_manager
                  ) || null
              }
              onChange={(selectedOption) =>
                handleChange({
                  target: {
                    name: "project_manager",
                    value: selectedOption?.value || "",
                  },
                })
              }

              isMulti={false}
              isClearable={true}
              placeholder="Select Project Manager"
              className="react-select-container"
              classNamePrefix="select"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mt-4">
          {/* Submit Button */}
          <button
            onClick={() => handleFilter(1)}
            className="bg-blue-600 text-white font-semibold py-2 px-6 rounded-md hover:bg-blue-700 transition"
          >
            Submit
          </button>

          {/* Reset Button */}
          <button
            onClick={resetFilters}
            className="bg-gray-200 text-gray-800 font-semibold py-2 px-6 rounded-md hover:bg-gray-300 transition"
          >
            Reset
          </button>

          {/* <button
            onClick={downloadPDF}
            className="bg-gray-600 text-white font-semibold py-2 px-6 rounded-md hover:bg-gray-500 transition"
          >
            PDF
          </button> */}
        </div>
      </div>
      {/* <div
        className={`p-4 rounded-2xl   overflow-x-auto  bg-white transition-all 
               `}
      >
        <div className="flex justify-between items-center   ">



        </div>

        <table className="min-w-full text-sm text-left border-separate border-spacing-0 rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-gradient-to-r from-blue-50 to-blue-100 text-gray-700">
              <th className="p-3 border-b font-semibold">
                S.No
              </th>
              <th className="p-3 border-b font-semibold">
                Type of Work
              </th>
              <th className="p-3 border-b font-semibold">Count</th>
              <th className="p-3 border-b font-semibold">Amount</th>
              <th className="p-3 border-b font-semibold">Win</th>
              <th className="p-3 border-b font-semibold">Win Amount</th>
              <th className="p-3 border-b font-semibold">Loss</th>
              <th className="p-3 border-b font-semibold">
                Loss Amount
              </th>
              <th className="p-3 border-b font-semibold">Follow-up</th>
              <th className="p-3 border-b font-semibold">
                Follow-up Amount
              </th>

            </tr>
          </thead>
          <tbody>
            {quotationAmountByTypeOfWork?.length > 0 ? (
              quotationAmountByTypeOfWork?.map((item, idx) => (
                <tr
                  key={idx}
                  className={`transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } hover:bg-blue-50`}
                >
                  <td className="p-3 border-b">{idx + 1}</td>
                  <td
                    className="p-3 border-b cursor-pointer hover:text-blue-600"
                    onClick={() =>
                      navigate("/crm/quotation", {
                        state: {
                          // typeOfWorkName: item.type_of_work,
                          typeOfWorkFilter: item.type_of_work,
                        },
                      })
                    }
                  >
                    {item.type_of_work}
                  </td>
                  <td className="p-3 border-b">{item.count}</td>
                  <td className="p-3 border-b font-semibold text-blue-600">
                    ₹{item.amount?.toLocaleString("en-IN")}
                  </td>
                  <td className="p-3 border-b text-green-600 font-medium">
                    {item.win}
                  </td>
                  <td className="p-3 border-b text-green-600 font-medium">
                    {item.win_amount?.toLocaleString("en-IN")}
                  </td>
                  <td className="p-3 border-b text-red-600 font-medium">
                    {item.loss}
                  </td>
                  <td className="p-3 border-b text-red-600 font-medium">
                    {item.loss_amount?.toLocaleString("en-IN")}
                  </td>
                  <td className="p-3 border-b text-yellow-600 font-medium">
                    {item.followup}
                  </td>
                  <td className="p-3 border-b text-gray-600 font-medium">
                    {item.followup_amount?.toLocaleString("en-IN")}
                  </td>
                 
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="p-3 border-b text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div> */}

      <div className=" p-4 rounded-lg ">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <div
            className={`bg-white border rounded-lg shadow overflow-hidden ${fullscreenTable === "typeOfWork"
              ? "fixed inset-0 z-50 overflow-auto"
              : ""
              }`}
          >
            <div className="flex justify-between items-center bg-purple-100 p-2">
              <h3 className="text-sm sm:text-lg font-semibold text-purple-700">
                Type Of Work Quotation List
              </h3>
              <div className="flex gap-2">
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() => downloadExcel("quotationStatusByTypeOfWork", "Type Of Work Quotation")}
                  title="Download Excel"
                >
                  <TableCellsIcon className="h-5 w-5 text-white" />
                </button>
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() => downloadPDF("quotationStatusByTypeOfWork", "Type Of Work Quotation")}
                  title="Download PDF"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 text-white" />
                </button>
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() =>
                    setFullscreenTable(
                      fullscreenTable === "typeOfWork" ? null : "typeOfWork"
                    )
                  }
                  title={
                    fullscreenTable === "typeOfWork"
                      ? "Close Fullscreen"
                      : "Fullscreen"
                  }
                >
                  {fullscreenTable === "typeOfWork" ? (
                    <XMarkIcon className="h-5 w-5 text-white" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-5 w-5 text-white" />
                  )}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <div
                className={`overflow-y-auto ${fullscreenTable === "typeOfWork"
                  ? "max-h-[calc(100vh-80px)]" // full height minus header
                  : "max-h-[400px]" // normal height
                  }`}
              >
                <table className="min-w-[700px] w-full border-collapse">
                  <thead className="bg-gray-100 ">
                    <tr className="text-center font-semibold border-b">
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        S.No
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Type of Work
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Count
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Amount
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Win
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Win Amount
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Loss
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Loss Amount
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Follow-up
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Follow-up Amount
                      </th>
                      {/* <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">Negotiation</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {quotationAmountByTypeOfWork?.length > 0 ? (
                      quotationAmountByTypeOfWork?.map((item, idx) => (
                        <tr
                          key={idx}
                          className="text-center text-sm border-b hover:bg-gray-50"
                        >
                          <td className="p-2 border-r last:border-r-0">
                            {idx + 1}
                          </td>
                          <td className="p-2 border-r last:border-r-0 break-words">
                            {item.type_of_work}
                          </td>
                          <td className="p-2 border-r last:border-r-0 break-words">
                            {item.count}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            ₹{item.amount?.toLocaleString("en-IN")}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item.win}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item.win_amount?.toLocaleString("en-IN")}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item.loss}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item.loss_amount?.toLocaleString("en-IN")}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item.followup}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item.followup_amount?.toLocaleString("en-IN")}
                          </td>
                          {/* <td className="p-3 border-b text-gray-600 font-medium">
                        {item.negotiation}
                      </td> */}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="p-3 border-b text-center">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div
            className={`bg-white border rounded-lg shadow overflow-hidden ${fullscreenTable === "today"
              ? "fixed inset-0 z-50 overflow-auto"
              : ""
              }`}
          >
            <div className="flex justify-between items-center bg-green-100 p-2">
              <h3 className="text-sm sm:text-lg font-semibold text-green-700">
                Today's Follow-up Quotation List
              </h3>
              <div className="flex gap-2">
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() => downloadExcel("todayQuotationList", "Today's Quotation")}
                  title="Download Excel"
                >
                  <TableCellsIcon className="h-5 w-5 text-white" />
                </button>
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() => downloadPDF("todayQuotationList", "Today's Quotation")}
                  title="Download PDF"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 text-white" />
                </button>
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() =>
                    setFullscreenTable(
                      fullscreenTable === "today" ? null : "today"
                    )
                  }
                  title={
                    fullscreenTable === "today"
                      ? "Close Fullscreen"
                      : "Fullscreen"
                  }
                >
                  {fullscreenTable === "today" ? (
                    <XMarkIcon className="h-5 w-5 text-white" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-5 w-5 text-white" />
                  )}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <div
                className={`overflow-y-auto ${fullscreenTable === "today"
                  ? "max-h-[calc(100vh-80px)]" // full height minus header
                  : "max-h-[400px]" // normal height
                  }`}
              >
                <table className="min-w-[700px] w-full border-collapse">
                  <thead className="bg-gray-100 ">
                    <tr className="text-center font-semibold border-b">
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        S.No
                      </th>



                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Enquiry No
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Quotation No
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Party Name
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Mobile No
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Type Of Work
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Project Manager
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Sales/Service
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Send Status
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {todayQuotationList?.length > 0 ? (
                      todayQuotationList?.map((item, idx) => (
                        <tr
                          key={idx}
                          className="text-center text-sm border-b hover:bg-gray-50"
                        >
                          <td className="p-2 border-r last:border-r-0">
                            {idx + 1}
                          </td>
                          <td className="p-2 border-r last:border-r-0 ">
                            {item.enquiry_no || "N/A"}
                          </td>
                          <td className="p-2 border-r last:border-r-0 ">
                            {item.quote_number || "N/A"}
                          </td>

                          <td
                            className="p-2 border-r last:border-r-0 break-words"
                          // onClick={() =>
                          //   navigate("/crm/quotation", {
                          //     state: {
                          //       // typeOfWorkName: item.type_of_work,
                          //       typeOfWorkFilter: item.type_of_work,
                          //     },
                          //   })
                          // }
                          >
                            {item.party_name || "N/A"}
                          </td>

                          <td className="p-2 border-r text-blue-500 last:border-r-0 ">

                             {item?.mobile_no ? (
                              <a href={`tel:${item.mobile_no}`}>
                                {item.mobile_no}
                              </a>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="p-2 border-r last:border-r-0  ">
                            <span
                              className="text-xs rounded px-1"
                              style={{
                                color: todayRandomColorMap[item.type_of_work],
                                border: `1px solid ${todayRandomColorMap[item.type_of_work]
                                  }`,
                              }}
                            >
                              {item?.type_of_work || "N/A"}
                            </span>
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item.project_manager || "N/A"}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item.sales_service || "N/A"}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item.quote_sent_status}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item.status || "N/A"}
                          </td>

                          {/* <td className="p-3 border-b text-gray-600 font-medium">
                        {item.negotiation}
                      </td> */}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="p-3 border-b text-center">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div
            className={`bg-white border rounded-lg shadow overflow-hidden ${fullscreenTable === "unfollowed"
              ? "fixed inset-0 z-50 overflow-auto"
              : ""
              }`}
          >
            <div className="flex justify-between items-center bg-red-100 p-2">
              <h3 className="text-sm sm:text-lg font-semibold text-red-700">
                Unfollowed Quotation List
              </h3>
              <div className="flex gap-2">
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() => downloadExcel("unFollowedQuotationList", "Unfollowed Quotation")}
                  title="Download Excel"
                >
                  <TableCellsIcon className="h-5 w-5 text-white" />
                </button>
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() => downloadPDF("unFollowedQuotationList", "Unfollowed Quotation")}
                  title="Download PDF"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 text-white" />
                </button>
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() =>
                    setFullscreenTable(
                      fullscreenTable === "unfollowed" ? null : "unfollowed"
                    )
                  }
                  title={
                    fullscreenTable === "unfollowed"
                      ? "Close Fullscreen"
                      : "Fullscreen"
                  }
                >
                  {fullscreenTable === "unfollowed" ? (
                    <XMarkIcon className="h-5 w-5 text-white" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-5 w-5 text-white" />
                  )}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <div
                className={`overflow-y-auto ${fullscreenTable === "unfollowed"
                  ? "max-h-[calc(100vh-80px)]" // full height minus header
                  : "max-h-[400px]" // normal height
                  }`}
              >
                <table className="min-w-[700px] w-full border-collapse">
                  <thead className="bg-gray-100 ">
                    <tr className="text-center font-semibold border-b">
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        S.No
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Enquiry No
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Quotation No
                      </th>

                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Party Name
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Mobile No
                      </th>


                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Type Of Work{" "}
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Project Manager
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Sales/Service{" "}
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Send Status
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Status
                      </th>

                      {/* <th className="p-3 border-b font-semibold">Negotiation</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {unfollowedQuotationList?.length > 0 ? (
                      unfollowedQuotationList?.map((item, idx) => (
                        <tr
                          key={idx}
                          className="text-center text-sm border-b hover:bg-gray-50"
                        >
                          <td className="p-2 border-r last:border-r-0">
                            {idx + 1}
                          </td>
                          <td className="p-2 border-r last:border-r-0 ">
                            {item?.enquiry_no}
                          </td>

                          <td className="p-2 border-r last:border-r-0 ">
                            {item?.quote_number}
                          </td>



                          <td
                            className="p-2 border-r last:border-r-0 break-words"
                          // onClick={() =>
                          //   navigate("/crm/quotation", {
                          //     state: {

                          //       typeOfWorkFilter: item.type_of_work,
                          //     },
                          //   })
                          // }
                          >
                            {item?.party_name || "N/A"}
                          </td>
                          <td className="p-2 border-r text-blue-500 last:border-r-0 ">

                            {item?.mobile_no ? (
                              <a href={`tel:${item.mobile_no}`}>
                                {item.mobile_no}
                              </a>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>

                          <td className="  p-2 border-r last:border-r-0">
                            <span
                              className="text-xs rounded px-1"
                              style={{
                                color: randomColorMap[item.type_of_work],
                                border: `1px solid ${randomColorMap[item.type_of_work]
                                  }`,
                              }}
                            >
                              {item?.type_of_work || "N/A"}
                            </span>
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item?.project_manager || "N/A"}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item?.sales_service || "N/A"}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item?.quote_sent_status}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item?.status || "N/A"}
                          </td>
                          {/* <td className="p-3 border-b text-gray-600 font-medium">
                        {item.negotiation}
                      </td> */}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="p-3 border-b text-center">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div
            className={`bg-white border rounded-lg shadow overflow-hidden ${fullscreenTable === "future"
              ? "fixed inset-0 z-50 overflow-auto"
              : ""
              }`}
          >
            <div className="flex justify-between items-center bg-stone-100 p-2">
              <h3 className="text-sm sm:text-lg font-semibold text-stone-700">
                Future Follow-up Quotation List
              </h3>
              <div className="flex gap-2">
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() => downloadExcel("futureQuotationList", "Future Quotation")}
                  title="Download Excel"
                >
                  <TableCellsIcon className="h-5 w-5 text-white" />
                </button>
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() => downloadPDF("futureQuotationList", "Future Quotation")}
                  title="Download PDF"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 text-white" />
                </button>
                <button
                  className=" rounded  bg-gray-600 py-2 px-3 "
                  onClick={() =>
                    setFullscreenTable(
                      fullscreenTable === "future" ? null : "future"
                    )
                  }
                  title={
                    fullscreenTable === "future"
                      ? "Close Fullscreen"
                      : "Fullscreen"
                  }
                >
                  {fullscreenTable === "future" ? (
                    <XMarkIcon className="h-5 w-5 text-white" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-5 w-5 text-white" />
                  )}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <div
                className={`overflow-y-auto ${fullscreenTable === "future"
                  ? "max-h-[calc(100vh-80px)]" // full height minus header
                  : "max-h-[400px]" // normal height
                  }`}
              >
                <table className="min-w-[700px] w-full border-collapse">
                  <thead className="bg-gray-100 ">
                    <tr className="text-center font-semibold border-b">
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        S.No
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Enquiry No
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Quotation No
                      </th>

                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Party Name
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Mobile No
                      </th>

                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Type Of Work{" "}
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Project Manager
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Sales/Service{" "}
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Send Status
                      </th>
                      <th className="p-2 border-r last:border-r-0 text-xs sm:text-sm text-gray-900 bg-gray-100">
                        Status
                      </th>

                      {/* <th className="p-3 border-b font-semibold">Negotiation</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {futureQuotationList?.length > 0 ? (
                      futureQuotationList?.map((item, idx) => (
                        <tr
                          key={idx}
                          className="text-center text-sm border-b hover:bg-gray-50"
                        >
                          <td className="p-2 border-r last:border-r-0">
                            {idx + 1}
                          </td>
                          <td className="p-2 border-r last:border-r-0 ">
                            {item?.enquiry_no}
                          </td>
                          <td className="p-2 border-r last:border-r-0 break-words">
                            {item?.quote_number}
                          </td>

                          <td
                            className="p-2 border-r last:border-r-0 break-words"
                          // onClick={() =>
                          //   navigate("/crm/quotation", {
                          //     state: {
                          //       // typeOfWorkName: item.type_of_work,
                          //       typeOfWorkFilter: item.type_of_work,
                          //     },
                          //   })
                          // }
                          >
                            {item?.party_name || "N/A"}
                          </td>
                          <td className="p-2 border-r text-blue-500 last:border-r-0">
                            {item?.mobile_no ? (
                              <a href={`tel:${item.mobile_no}`}>
                                {item.mobile_no}
                              </a>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>

                          <td className="p-2 border-r last:border-r-0">
                            <span
                              className="text-xs rounded px-1"
                              style={{
                                color: futureRandomColorMap[item.type_of_work],
                                border: `1px solid ${futureRandomColorMap[item.type_of_work]
                                  }`,
                              }}
                            >
                              {item?.type_of_work || "N/A"}
                            </span>
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item?.project_manager || "N/A"}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item?.sales_service || "N/A"}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item?.quote_sent_status || "N/A"}
                          </td>
                          <td className="p-2 border-r last:border-r-0">
                            {item?.status || "N/A"}
                          </td>
                          {/* <td className="p-3 border-b text-gray-600 font-medium">
                        {item.negotiation}
                      </td> */}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="p-3 border-b text-center">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationReportList;
